/**
 * UnifiedCoPilot - Context-driven AI Co-Pilot Component
 * 
 * A single, reusable component for all item and collection co-pilots.
 * Works strictly by context passed to it - no awareness of container.
 * 
 * Features:
 * - Admin-configurable quick actions, display name, welcome message
 * - User-scoped chat history
 * - Pending actions (proposed updates, splits, metrics)
 * - Error recovery with retry
 * - Unsaved changes warning
 * - Locked field handling (context + apply-time filtering)
 * - Stop generation (abort in-flight requests)
 * 
 * @example
 * <UnifiedCoPilot
 *   contextType="epic"
 *   contextId={epic.epic_id}
 *   productId={productId}
 *   contextData={formData}
 *   relatedData={{ product, personas, features }}
 *   onApplyUpdates={handleApplyUpdates}
 *   canEditField={canEditField}
 *   onRefresh={refreshEpicData}
 *   variant="item"
 * />
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Sparkles, Send, Trash2, Loader2, AlertTriangle, 
  Target, Layers, Lightbulb, Zap, Search, RefreshCw,
  MoreHorizontal, ChevronRight, X, Square
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useFeatureFlags, COPILOT_CONTEXT_FLAGS } from '../../hooks/useFeatureFlags';
import { useNavigate } from 'react-router-dom';

// Hooks
import { useCoPilotConfig, useCoPilotDraft, useCoPilotHistory } from './hooks';

// Shared modules (centralized)
import { useThinkingPhrases, handleAIChatError, ActionButtonsRow, filterRenderableActions } from './shared';

// Components
import CopilotMarkdown from './CopilotMarkdown';
import UnifiedActionReview from './UnifiedActionReview';

// API
import axios from 'axios';
import { unifiedCopilotApi, tokenApi } from '../../services/api';

// Context
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useModalZIndex } from '../../contexts/ModalZIndexContext';

// Collection types (plural) - use product_id as context_id
const COLLECTION_TYPES = new Set(['features', 'epics', 'goals', 'initiatives', 'personas', 'requests', 'strategy', 'market', 'ideation', 'kanban', 'releases', 'help']);

// Roles that can manage tokens
const TOKEN_MANAGEMENT_ROLES = new Set(['owner', 'billing_manager', 'admin']);

// Icon mapping for quick actions
const ICON_MAP = {
  Sparkles,
  Target,
  Layers,
  Lightbulb,
  Zap,
  Search,
  RefreshCw
};

/**
 * UnifiedCoPilot Component
 */
const UnifiedCoPilot = ({
  // Identity
  contextType,
  contextId,
  productId,
  
  // Data
  contextData = {},
  relatedData = {},
  
  // Callbacks (Required)
  onApplyUpdates,
  canEditField = () => true,
  onRefresh,
  
  // Optional Callbacks
  onSplitItem,
  onAddMetrics,
  onCollapse,  // Collapse callback for drawer
  
  // Optional Overrides
  quickActions: quickActionsOverride = null,
  welcomeMessage: welcomeMessageOverride = null,
  displayName: displayNameOverride = null,
  
  // Generic injectable context - passed directly to API
  // Frontend owns the label/formatting. Example use: Deep Research report content
  injectedContext = null,
  
  // Response handler - called before default action processing
  // Return { handled: true } to skip action buttons (e.g., for canvas/generative co-pilots)
  onResponseReceived = null,
  
  // Behavior
  variant = 'item',
  className = ''
}) => {
  // Check feature flag for this copilot
  const { isEnabled } = useFeatureFlags();
  const copilotFeatureFlag = COPILOT_CONTEXT_FLAGS[contextType];
  const isCopilotEnabled = !copilotFeatureFlag || isEnabled(copilotFeatureFlag);
  const navigate = useNavigate();
  
  // Contexts - must be called unconditionally
  const { getThemeColors, getActivePersonality, getSystemPrompt } = useSafeMode();
  const { user } = useAuth();
  const colors = getThemeColors();
  const activePersonality = getActivePersonality();
  const parentZIndex = useModalZIndex(); // Get z-index from parent modal context (defaults to 50)
  
  // Config from admin — no fallbacks
  const { config, loading: configLoading, error: configError } = useCoPilotConfig(contextType);
  
  // Draft persistence
  const { draft, updateDraft, clearDraft } = useCoPilotDraft(contextType, contextId);
  
  // Chat history - simplified: pending actions now stored in messages
  const {
    messages,
    setMessages,
    loading: historyLoading,
    loadHistory,
    addLocalMessage,
    removeLastMessage,
    logActionOutcome,
    clearHistory,
    addSummary
  } = useCoPilotHistory(contextType, contextId);
  
  // Local state - simplified: no more pendingAction/proposedUpdates state
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [hasAIActivity, setHasAIActivity] = useState(false);
  const [sessionActions, setSessionActions] = useState([]);
  
  // Rotating thinking phrases for loading state
  const { phrase: thinkingPhrase, isVisible: phraseVisible } = useThinkingPhrases(chatLoading);
  
  // Token budget state
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(true);
  
  const chatEndRef = useRef(null);
  const abortControllerRef = useRef(null); // For cancelling in-flight requests
  
  // Check if ANY message has unresolved actions (blocks chat input)
  const hasPendingAction = useMemo(() => {
    return messages.some(m => {
      if (m.role !== 'assistant' || !m.pending_action?.actions?.length) return false;
      // Check if any action is not resolved
      return m.pending_action.actions.some(a => (a.type || '').toUpperCase() !== 'OUTCOME');
    });
  }, [messages]);
  
  // Check if user can manage tokens (owner/billing_manager/admin)
  const canManageTokens = TOKEN_MANAGEMENT_ROLES.has(user?.role);
  const isBudgetExceeded = budgetStatus?.hard_stopped === true;
  
  // Fetch budget status on mount and periodically
  useEffect(() => {
    const fetchBudgetStatus = async () => {
      try {
        const res = await tokenApi.getStatus();
        setBudgetStatus(res.data);
      } catch (err) {
        console.warn('Failed to fetch budget status:', err);
        // Don't block on budget check failure
        setBudgetStatus({ hard_stopped: false, status: 'unknown' });
      } finally {
        setBudgetLoading(false);
      }
    };
    
    fetchBudgetStatus();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchBudgetStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Context reports state (for market/strategy co-pilots)
  const [contextReports, setContextReports] = useState([]);
  
  // Fetch context reports for market/strategy co-pilots
  useEffect(() => {
    const fetchContextReports = async () => {
      if (!productId || !['market', 'strategy'].includes(contextType)) {
        setContextReports([]);
        return;
      }
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/research/reports/context/${productId}`,
          { params: { context_type: contextType } }
        );
        setContextReports(response.data.reports || []);
      } catch (err) {
        console.warn('Failed to fetch context reports:', err);
        setContextReports([]);
      }
    };
    
    fetchContextReports();
  }, [productId, contextType]);
  
  // Derived values — config is the single source of truth (error state guard above ensures config exists)
  const displayName = config?.display_name || contextType;
  const quickActions = config?.quick_actions || [];
  const enabledQuickActions = quickActions.filter(a => a.is_enabled !== false);
  
  // Welcome message — from admin config only
  const welcomeMessage = useMemo(() => {
    if (config?.welcome_message) {
      return config.welcome_message.replace('{itemType}', contextType);
    }
    return null;
  }, [config?.welcome_message, contextType]);
  
  // Load history on mount/context change
  useEffect(() => {
    if (contextType && contextId) {
      // Reset session state on context change
      setSessionActions([]);
      setHasAIActivity(false);
      
      // Load data
      loadHistory();
    }
  }, [contextType, contextId, loadHistory]);
  
  // Initialize chat input from draft
  useEffect(() => {
    setChatInput(draft);
  }, [draft]);
  
  // Show welcome message if no history
  useEffect(() => {
    if (!historyLoading && messages.length === 0 && welcomeMessage) {
      setMessages([{
        role: 'assistant',
        content: welcomeMessage
      }]);
    }
  }, [historyLoading, messages.length, welcomeMessage, setMessages]);
  
  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);
  
  // PHASE C: buildAIContext removed - backend now handles context building from admin settings
  // SIMPLIFIED: handleAIAction removed - actions now stored directly in message by backend
  
  // Stop generation - abort in-flight request
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);
  
  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Send message to AI
  const sendMessage = useCallback(async (messageText) => {
    if (!messageText?.trim() || chatLoading || hasPendingAction) return;
    
    const text = messageText.trim();
    setChatLoading(true);
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    // RULE 3: Optimistic UI only - add to display, NOT to database
    // Backend will persist atomically on success
    addLocalMessage('user', text, {
      author_id: user?.user_id,
      author_name: user?.name || user?.email || 'You'
    });
    setChatInput('');
    clearDraft();
    setHasAIActivity(true);
    
    try {
      // Use unified endpoint for ALL co-pilot types
      // Include injected_context if provided (e.g., research report content)
      const chatPayload = {
        message: text,
        conversation_history: messages.slice(-20) // Backend persists both messages atomically
      };
      if (injectedContext) {
        chatPayload.injected_context = injectedContext;
      }
      // For item variants, pass current form data so AI sees unsaved changes
      // This allows real-time context awareness without requiring save first
      if (variant === 'item' && contextData && Object.keys(contextData).length > 0) {
        chatPayload.form_data_override = contextData;
      }
      const res = await unifiedCopilotApi.chat(contextType, contextId, chatPayload, { signal: abortControllerRef.current.signal });
      
      // Backend returns: { response: "message text", actions: [...], assistant_message_id: "msg_xxx" }
      // AND stores the message with pending_action if actions exist
      const displayMessage = res.data.response || "I couldn't process that request.";
      const allActions = res.data.actions || [];
      const assistantMessageId = res.data.assistant_message_id; // Unique ID from backend
      
      // Allow parent to handle response first (e.g., for canvas/generative co-pilots)
      // If handled, skip default action processing but still show the message
      let skipActionProcessing = false;
      if (onResponseReceived) {
        const result = onResponseReceived(res.data);
        if (result?.handled) {
          skipActionProcessing = true;
        }
      }
      
      // Filter out non-renderable actions
      const renderableActions = skipActionProcessing ? [] : filterRenderableActions(allActions);
      
      // Build assistant message with all actions in pending_action
      const assistantMessage = {
        message_id: assistantMessageId, // Use the ID from backend for reliable identification
        role: 'assistant',
        content: displayMessage,
        timestamp: new Date().toISOString(),
      };
      
      // If there are actions, add them as pending_action with tag
      if (renderableActions.length > 0) {
        assistantMessage.pending_action = {
          tag: 'PENDING_REVIEW',
          actions: renderableActions
        };
        
        // Track session actions
        const firstAction = renderableActions[0];
        if (firstAction?.type) {
          setSessionActions(prev => [...prev, firstAction.type]);
        }
      }
      
      // Add message to local state
      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (err) {
      // Handle abort/cancellation separately
      if (err.name === 'AbortError' || err.name === 'CanceledError' || axios.isCancel?.(err)) {
        // User cancelled - remove optimistic message, add stopped indicator
        removeLastMessage('user', text);
        addLocalMessage('user', text, {
          author_id: user?.user_id,
          author_name: user?.name || user?.email || 'You'
        });
        addLocalMessage('assistant', '_Generation stopped by user._', {
          isStoppedGeneration: true
        });
        return;
      }
      
      console.error('AI chat error:', err);
      
      // RULE 2: Failed Turn Recovery - remove optimistic message, restore to input
      removeLastMessage('user', text);
      setChatInput(text);
      
      // Handle HTTP 402 (Payment Required) errors
      if (err.response?.status === 402) {
        const detail = err.response?.data?.detail;
        const isTokenBudget = typeof detail === 'string' 
          ? detail.toLowerCase().includes('token budget')
          : detail?.message?.toLowerCase().includes('token budget');
        
        if (isTokenBudget) {
          setBudgetStatus(prev => ({ ...prev, hard_stopped: true, status: 'exceeded' }));
          
          if (canManageTokens) {
            toast.error(
              <div>
                <strong>Token budget exceeded</strong>
                <p className="text-sm mt-1">
                  <a href="/settings" className="underline text-blue-400 hover:text-blue-300">
                    Add more tokens in Settings
                  </a>
                </p>
              </div>,
              { duration: 8000 }
            );
          } else {
            toast.error(
              'Token budget exceeded. Contact your account owner to add more tokens.',
              { duration: 8000 }
            );
          }
          return;
        }
        
        const errorType = detail?.error;
        if (errorType === 'seat_limit_exceeded') {
          toast.error('Seat limit exceeded. Contact your account owner to upgrade.');
        } else if (errorType === 'upgrade_required') {
          toast.error('Plan upgrade required. Contact your account owner.');
        } else {
          toast.error('Payment required. Please check your account settings.');
        }
        return;
      }
      
      // Handle HTTP 503 (Service Unavailable) - AI service overloaded
      if (err.response?.status === 503) {
        const detail = err.response?.data?.detail;
        const message = detail?.message || "Google's Gemini AI service is currently overloaded. Please try again in 1-2 minutes.";
        toast.error(message, { duration: 8000 });
        return;
      }
      
      toast.error('AI request failed - your message has been restored. Please try again.');
    } finally {
      setChatLoading(false);
      abortControllerRef.current = null;
    }
  }, [
    chatLoading, hasPendingAction, addLocalMessage, removeLastMessage, clearDraft,
    contextType, contextId, messages, canManageTokens, user, setMessages, injectedContext,
    variant, contextData, onResponseReceived
  ]);
  
  // Handle input change with draft persistence
  const handleInputChange = (e) => {
    const value = e.target.value;
    setChatInput(value);
    updateDraft(value);
  };
  
  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(chatInput);
    }
  };
  
  // Quick action click
  const handleQuickAction = (action) => {
    const prompt = action.prompt?.replace('{itemType}', contextType) || action.label;
    sendMessage(prompt);
  };
  
  // Clear chat input and draft
  const clearChatInput = useCallback(() => {
    setChatInput('');
    clearDraft();
  }, [clearDraft]);
  
  // Clear all history
  const handleClearHistory = async () => {
    try {
      await clearHistory();
      setShowClearConfirm(false);
      
      // Show welcome message again (if configured)
      if (welcomeMessage) {
        setMessages([{
          role: 'assistant',
          content: welcomeMessage
        }]);
      } else {
        setMessages([]);
      }
      
      toast.success('Chat history cleared');
    } catch {
      toast.error('Failed to clear history');
    }
  };
  
  // Format relative time
  const formatTime = (timestamp) => {
    if (!timestamp) return null;
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };
  
  // Render icon for quick action
  const renderIcon = (iconName) => {
    const Icon = ICON_MAP[iconName] || Sparkles;
    return <Icon className="w-3 h-3" />;
  };
  
  // If copilot is disabled via feature flag, don't render
  // NOTE: This must come AFTER all hooks to comply with React rules-of-hooks
  if (!isCopilotEnabled) {
    return null;
  }
  
  // Loading state
  if (configLoading || historyLoading) {
    return (
      <div className={cn("flex flex-col h-full items-center justify-center", colors.bgTertiary, className)}>
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
        <p className={cn("text-sm mt-2", colors.textMuted)}>Loading co-pilot...</p>
      </div>
    );
  }

  // Error state — config failed to load. No silent degradation.
  if (configError) {
    return (
      <div data-testid="copilot-config-error" className={cn("flex flex-col h-full items-center justify-center p-6", colors.bgTertiary, className)}>
        <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
        <p className={cn("text-sm font-medium mb-1", colors.textPrimary)}>Copilot Not Configured</p>
        <p className={cn("text-xs text-center mb-4 max-w-xs", colors.textMuted)}>{configError}</p>
        <Button data-testid="copilot-config-retry" variant="outline" size="sm" onClick={() => window.location.reload()}>
          <RefreshCw className="w-3 h-3 mr-1" />
          Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className={cn("flex flex-col h-full", colors.bgTertiary, className)}>
      {/* Header */}
      <div className={cn("p-4 border-b", colors.borderPrimary)}>
        {/* Top row: Collapse, Icon, Name, Clear History (right-aligned) */}
        <div className="flex items-center gap-2 mb-3">
          {/* Collapse button - circular style matching sidebar */}
          {onCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onCollapse}
              className={cn(
                "w-6 h-6 rounded-full shadow-sm transition-all border",
                "hover:scale-110",
                colors.bgSecondary,
                colors.borderPrimary,
                colors.bgHover
              )}
              title="Collapse panel"
            >
              <ChevronRight className={cn("w-3 h-3", colors.textMuted)} />
            </Button>
          )}
          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-lg", colors.bgActive)}>
            {activePersonality?.icon || '🤖'}
          </div>
          <div>
            <p className={cn("text-sm font-medium", colors.textPrimary)}>{displayName}</p>
            <p className={cn("text-xs", colors.textMuted)}>AI Co-Pilot</p>
          </div>
          
          {/* Spacer to push trash to the right */}
          <div className="flex-1" />
          
          {/* Clear History - aligned right */}
          {messages.length > 1 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowClearConfirm(true)}
              className={cn("text-xs", colors.textMuted)}
              title="Clear chat history"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        
        {/* Bottom row: Quick Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Quick Actions - show first 2 as buttons */}
          {enabledQuickActions.slice(0, 2).map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant="outline"
              onClick={() => handleQuickAction(action)}
              disabled={chatLoading}
              className={cn("text-xs gap-1.5", colors.borderPrimary)}
            >
              {renderIcon(action.icon)}
              {action.label}
            </Button>
          ))}
          
          {/* More quick actions dropdown */}
          {enabledQuickActions.length > 2 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={chatLoading}
                  className={cn("text-xs gap-1", colors.borderPrimary)}
                >
                  <MoreHorizontal className="w-3 h-3" />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {enabledQuickActions.slice(2).map((action) => (
                  <DropdownMenuItem 
                    key={action.id}
                    onClick={() => handleQuickAction(action)}
                    className="text-xs gap-2"
                  >
                    {renderIcon(action.icon)}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, i) => {
          const timeLabel = formatTime(msg.timestamp);
          
          // System message styling (warnings) - per Visual Spec Section 2
          if (msg.role === 'system') {
            return (
              <div key={i} className="flex justify-start">
                <div className={cn(
                  "max-w-[90%] rounded-lg px-3 py-2 text-sm",
                  "bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700",
                  "text-amber-800 dark:text-amber-200"
                )}>
                  {msg.content}
                </div>
              </div>
            );
          }
          
          // User message or assistant message
          return (
            <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div className="max-w-[90%]">
                {/* Time label for user messages */}
                {msg.role === 'user' && timeLabel && (
                  <div className={cn("text-[10px] mb-0.5 text-right", colors.textMuted)}>
                    {timeLabel}
                  </div>
                )}
                <div className={cn(
                  "rounded-lg px-3 py-2 text-sm",
                  msg.role === 'user' 
                    ? cn(colors.accentGradient, "text-white", "rounded-tr-sm")
                    : cn(colors.bgCard, colors.textSecondary, "border", colors.borderPrimary)
                )}>
                  {msg.role === 'assistant' ? (
                    <CopilotMarkdown 
                      content={msg.content}
                      excludeEntityId={contextId}
                      stripExternalLinks={contextType === 'help'}
                      className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:mb-3 [&>ol]:mb-3 [&>ul]:pl-4 [&>ol]:pl-4 [&>li]:mb-1 [&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mt-3 [&>h3]:mb-2"
                    />
                  ) : (
                    msg.content
                  )}
                  
                  {/* Inline action buttons for assistant messages */}
                  {msg.role === 'assistant' && msg.actions && msg.actions.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <ActionButtonsRow 
                        actions={msg.actions} 
                        onAction={(action) => {
                          // Handle navigational actions
                          if ((action.type || action.action || '').toUpperCase() === 'OPEN_ENTITY') {
                            // Navigate to entity
                            const entityType = action.entity_type;
                            const entityId = action.entity_id;
                            if (entityType && entityId) {
                              window.location.href = `/products/${productId}?tab=${entityType}s&highlight=${entityId}`;
                            }
                          }
                        }} 
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Inline UnifiedActionReview for this message's pending_action */}
              {msg.role === 'assistant' && msg.pending_action?.actions?.length > 0 && (
                <div className="w-full mt-2">
                  <UnifiedActionReview
                    actions={msg.pending_action.actions}
                    productId={productId}
                    contextType={contextType}
                    contextId={contextId}
                    user={user}
                    parentZIndex={parentZIndex}
                    relatedData={relatedData}
                    logActionOutcome={(outcomeType, summary, metadata, options) => {
                      // Pass the message_id for reliable identification
                      return logActionOutcome(outcomeType, summary, metadata, {
                        ...options,
                        messageId: msg.message_id
                      });
                    }}
                    onSplitItem={onSplitItem}
                    onApplyUpdates={onApplyUpdates}
                    variant={variant}
                    onSuccess={async () => {
                      if (onRefresh) {
                        await onRefresh();
                      }
                    }}
                    onDismiss={() => {}}
                  />
                </div>
              )}
            </div>
          );
        })}
        
        {/* Loading indicator with rotating thinking phrases */}
        {chatLoading && (
          <div className="flex justify-start" data-testid="copilot-thinking-indicator">
            <div className={cn("rounded-lg px-3 py-2 flex items-center gap-2", colors.bgCard)}>
              <Loader2 className={cn("w-4 h-4 animate-spin flex-shrink-0", colors.textMuted)} />
              <span 
                className={cn(
                  "text-sm italic transition-opacity duration-200",
                  colors.textMuted,
                  phraseVisible ? "opacity-100" : "opacity-0"
                )}
              >
                {thinkingPhrase}
              </span>
            </div>
          </div>
        )}
        
        <div ref={chatEndRef} />
      </div>
      
      {/* Input Area */}
      <div className={cn("p-3 border-t", colors.borderPrimary)}>
        {/* Context Reports Tag - shows when research reports are influencing this co-pilot */}
        {contextReports.length > 0 && (
          <div className={cn("mb-2 text-xs", colors.textMuted)}>
            <span>📎 </span>
            {(() => {
              const hasMarketAnalysis = contextReports.some(r => r.research_type === 'market-analysis');
              const hasCompetitorIntel = contextReports.some(r => r.research_type === 'competitor-intel');
              const baseUrl = `/products/${productId}?tab=ideation&space=`;
              
              const handleNavigate = (space) => (e) => {
                e.preventDefault();
                navigate(`${baseUrl}${space}`);
              };
              
              if (hasMarketAnalysis && hasCompetitorIntel) {
                return (
                  <>
                    A <button onClick={handleNavigate('market-analysis')} className="text-blue-500 hover:underline">Market Analysis</button>
                    {' '}and{' '}
                    <button onClick={handleNavigate('competitor-intel')} className="text-blue-500 hover:underline">Competitor Intelligence</button>
                    {' '}report included in context.
                  </>
                );
              } else if (hasMarketAnalysis) {
                return (
                  <>
                    A <button onClick={handleNavigate('market-analysis')} className="text-blue-500 hover:underline">Market Analysis</button>
                    {' '}report included in context.
                  </>
                );
              } else if (hasCompetitorIntel) {
                return (
                  <>
                    A <button onClick={handleNavigate('competitor-intel')} className="text-blue-500 hover:underline">Competitor Intelligence</button>
                    {' '}report included in context.
                  </>
                );
              }
              return null;
            })()}
          </div>
        )}
        
        {/* Budget Exceeded Warning */}
        {isBudgetExceeded && (
          <div className={cn(
            "mb-3 p-3 rounded-lg border text-sm",
            "bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400"
          )}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Token budget exceeded</p>
                {canManageTokens ? (
                  <p className="text-xs mt-1 opacity-80">
                    <a href="/settings" className="underline hover:no-underline">
                      Add more tokens in Settings
                    </a>{' '}
                    to continue using AI features.
                  </p>
                ) : (
                  <p className="text-xs mt-1 opacity-80">
                    Contact your account owner to add more tokens.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-2">
          <Textarea
            value={chatInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={
              hasPendingAction 
                ? "Resolve pending action above to continue..." 
                : isBudgetExceeded 
                  ? "AI features disabled - token budget exceeded" 
                  : "Ask AI to help..."
            }
            rows={4}
            disabled={chatLoading || isBudgetExceeded || hasPendingAction}
            className={cn("flex-1 resize-none text-sm", colors.bgInput, colors.borderPrimary)}
          />
          {chatLoading ? (
            <Button
              onClick={stopGeneration}
              variant="destructive"
              className="self-end"
              title="Stop generation"
            >
              <Square className="w-4 h-4 fill-current" />
            </Button>
          ) : (
            <Button
              onClick={() => sendMessage(chatInput)}
              disabled={!chatInput.trim() || isBudgetExceeded || hasPendingAction}
              className={cn("self-end", colors.accentGradient)}
              title={
                hasPendingAction 
                  ? "Resolve pending action first" 
                  : isBudgetExceeded 
                    ? "Token budget exceeded" 
                    : "Send message"
              }
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>
        {/* Helper text with Clear Contents link (Rule 11) */}
        <p className={cn("text-xs mt-1", colors.textMuted)}>
          {chatLoading ? (
            <span className="text-amber-500">Generating... click Stop to cancel</span>
          ) : hasPendingAction ? (
            <span className="text-amber-500">Apply or dismiss changes above to continue</span>
          ) : (
            <>
              Press Enter to send, Shift+Enter for newline
              {chatInput.trim() && (
                <>
                  <span className="mx-1">|</span>
                  <button
                    type="button"
                    onClick={clearChatInput}
                    className="text-blue-500 hover:text-blue-600 hover:underline"
                  >
                    Clear Contents
                  </button>
                </>
              )}
            </>
          )}
        </p>
      </div>
      
      {/* Clear History Confirmation */}
      {showClearConfirm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={cn("rounded-lg p-4 max-w-sm mx-4", colors.bgCard)}>
            <h3 className={cn("font-medium mb-2", colors.textPrimary)}>Clear Chat History?</h3>
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              This will clear your conversation history. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearHistory}
              >
                Clear History
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedCoPilot;
